<script context="module" lang="ts">
  export interface PickerItem {
    slug: string;
    label: string;
    means?: string;
  }

  export interface PickerGroup {
    key: string;
    label: string;
    items: PickerItem[];
  }
</script>

<script lang="ts">
  // The grouped picker the technology and the certification filters share:
  // a search box, groups that open and close, a row per entry that fills with
  // accent when chosen, and an (i) that explains it. Several entries in one
  // group mean any of them, several groups all of them; the host says so in
  // `anyOf` and owns the selection.
  import { Check, ChevronDown, ChevronRight, Info } from 'lucide-svelte';

  export let groups: PickerGroup[] = [];
  export let selected: string[] = [];
  /** Called with the slug of the row that was clicked. */
  export let onPick: (_slug: string) => void;
  export let placeholder = '';
  export let anyOf = '';
  export let embedded = false;
  /** Bound by the host, which hides what does not match a search. */
  export let search = '';

  let open: Record<string, boolean> = {};

  /**
   * Which row's explanation is showing, by slug. One at a time.
   *
   * # Why this is not a `title`
   *
   * It was one, and the text did not appear — in the panel or the assistant.
   * A native tooltip is the browser's to draw, on its own delay, and it is not
   * a thing this app uses anywhere else: the sliders and the result rows all
   * reveal their own.
   *
   * # And why it is not the app's floating tooltip either
   *
   * Those are absolutely positioned with `opacity-0` and `group-hover`, which
   * works where there is room to float into. The sidebar's technology list is
   * `max-h-56 overflow-y-auto`, and a scroll container clips on both axes — a
   * tooltip above a row near the top edge would be cut off, which is the same
   * complaint again in a narrower case.
   *
   * So the text appears inline, below the row, inside the cell that is already
   * scrolling. Nothing to clip it, and it costs a line of layout while it is
   * open.
   *
   * Hover and focus both, so it is reachable without a mouse — a tap gives the
   * button focus, which is what makes this work on a phone where hover does not
   * exist at all.
   */
  let explaining: string | null = null;

  /**
   * The panel's checkbox, in the one shape the bar uses.
   *
   * These were bare `h-3.5 w-3.5` inputs — the browser's own control, square
   * and blue, beside a sidebar full of rounded accent-coloured ones. With 251
   * of them it was the most visible styling in the panel and the only part of
   * it that did not belong.
   *
   * Sized the way ContactFilters sizes its channels: 12px in the 320px rail,
   * where it sits among a dozen sections, and 16px in a guided step, where the
   * same box reads as an afterthought and a 12px hit target is a poor one.
   */
  $: box = embedded ? 'h-4 w-4' : 'h-3 w-3';

  /**
   * The step has the width of the card; the rail has 320px.
   *
   * Sixteen groups in one narrow column is a scroll, and a group of
   * seventy-three in a 224px-tall scroller inside it is a scroll within a
   * scroll. Embedded, the groups sit two abreast and their contents three, the
   * way ContactFilters spreads its channels, and nothing is capped — a wizard
   * step can be as tall as its question needs.
   */
  $: groupGrid = embedded ? 'grid gap-2 sm:grid-cols-2' : 'space-y-1';
  //
  // Two columns inside a group and no more, because the labels are long: HIN
  // Health Info Net, SAP SuccessFactors, Microsoft Entra ID, Adobe Commerce
  // Cloud. At three the group cards are half the card's width each, so a
  // column is under 200px and most of these truncate — and a filter list where
  // the names are cut off is a list nobody can choose from.
  //
  // The gaps are the picker's `gap-1.5`, not the old `gap-x-6 gap-y-1`. That
  // pairing was tuned for bare rows, where a wide column gutter is what keeps
  // two lists apart; now that each row is a bordered card, its own edge does
  // that and a 24px gutter only wastes the width the labels need. `grid-cols-1`
  // is written out for the reason the picker writes it out: a bare `grid`
  // already stacks, so saying it makes the single column the intended base case
  // rather than something to be overridden by accident later.
  $: itemGrid = embedded
    ? 'grid grid-cols-1 gap-1.5 px-3 pb-3 sm:grid-cols-2'
    : 'grid grid-cols-1 gap-1 max-h-56 overflow-y-auto px-2 pb-2';
  $: headerPad = embedded ? 'px-3 py-2.5 text-sm' : 'px-2 py-1.5 text-sm';

  $: needle = search.trim().toLowerCase();

  /**
   * A search box, because 244 checkboxes is not a list anybody reads.
   *
   * It matches the label rather than the slug: a customer looking for Mailchimp
   * types Mailchimp, and the slug is our identifier, not a name they have ever
   * seen.
   */
  /**
   * A group's entries, alphabetically, so a long one can be scanned by name.
   * Numbers by value, so ISO 9001 comes before ISO 14001; the groups keep
   * the server's order.
   */
  const byLabel = (a: PickerItem, b: PickerItem) =>
    a.label.localeCompare(b.label, 'de', { numeric: true, sensitivity: 'base' });

  function matching(items: PickerItem[]): PickerItem[] {
    const shown = needle ? items.filter((t) => t.label.toLowerCase().includes(needle)) : items;
    return [...shown].sort(byLabel);
  }

  /**
   * The groups to draw, each carrying whether it is open and how much of it is
   * chosen.
   *
   * # Both of those have to be computed here rather than called from the markup
   *
   * They were `expanded(key)` and `chosenIn(key)`, called from the template,
   * and neither worked: Svelte invalidates an expression when a variable *it
   * can see* changes, and all it sees there is the function name. It has no way
   * to know the body reads `open` or `$technologies`, so clicking a group's
   * plus updated the state and redrew nothing, and the count badge never moved.
   *
   * Computed in a reactive statement, the dependencies are visible — this block
   * names `groups`, `needle`, `open` and `$technologies` — so a change to any
   * of them redraws. The rule is that per-item state belongs on the item, not
   * behind a function the compiler cannot follow.
   *
   * `$technologies` is taken as an argument rather than read inside the map,
   * because a store cannot be subscribed to from a nested scope: the
   * subscription has to be at the top level of the component, which passing it
   * in is.
   *
   * Searching forces every matching group open, without which a search looks
   * broken: the hits are inside collapsed sections, so typing "mail" appears to
   * empty the panel.
   */
  $: visible = ((chosenSlugs: string[]) =>
    groups
      .map((g) => {
        const shown = matching(g.items);
        return {
          ...g,
          items: shown,
          open: needle !== '' || (open[g.key] ?? false),
          chosen: shown.filter((t) => chosenSlugs.includes(t.slug)).length
        };
      })
      .filter((g) => g.items.length > 0))(selected);

  function toggleGroup(key: string) {
    open = { ...open, [key]: !(open[key] ?? false) };
  }
</script>

<!--
      The industry picker's search box, down to the focus ring. This one had
      `rounded` where everything else in the app has `rounded-control`, half the
      padding, and no focus style at all — so the one control a customer types
      into gave no sign it had the caret.
    -->
<input
  type="search"
  bind:value={search}
  {placeholder}
  class="mb-3 w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
/>

<div class={groupGrid}>
  {#each visible as group (group.key)}
    <!--
          `self-start` so a group that is open does not stretch the one beside
          it to match. In a two-column grid the default makes both cells as
          tall as the taller, which draws a large empty box next to an open
          one.
        -->
    <div class="self-start rounded-control border border-hairline bg-surface-panel">
      <!--
            The industry picker's header row: a chevron that carries the colour,
            the name at the weight of a heading, and the count as a pill.
            Previously the whole row was `text-gray-700` on `text-gray-400`,
            which made sixteen group names read as sixteen lines of body text
            with no indication that any of them opened.

            The count was `text-blue-600` — a raw Tailwind blue, not the accent
            token every other emphasis in the panel uses, so it was a different
            blue from the checkboxes immediately below it.
          -->
      <button
        type="button"
        class="flex w-full items-center gap-2 text-left {headerPad}"
        aria-expanded={group.open}
        on:click={() => toggleGroup(group.key)}
      >
        {#if group.open}
          <ChevronDown class="h-4 w-4 shrink-0 text-accent-500" />
        {:else}
          <ChevronRight class="h-4 w-4 shrink-0 text-accent-500" />
        {/if}

        <!-- Truncates in the 320px rail, so the full name goes in a title. -->
        <span
          class="min-w-0 flex-1 truncate text-sm font-semibold text-ink-800"
          title={group.label}
        >
          {group.label}
        </span>

        {#if group.chosen > 0}
          <span
            class="shrink-0 rounded-full bg-accent-100 px-1.5 py-0.5 text-2xs font-semibold text-accent-700"
          >
            {group.chosen}
          </span>
        {/if}
      </button>

      {#if group.open}
        <!--
              The rule, said where the customer is about to rely on it rather
              than in a tooltip somewhere else.
            -->
        <p class="{embedded ? 'px-3' : 'px-2'} pb-1 text-xs text-gray-400">
          {anyOf}
        </p>
        <div class={itemGrid}>
          {#each group.items as tech (tech.slug)}
            {@const picked = selected.includes(tech.slug)}

            <!--
                  A row, in the industry picker's shape: a bordered card that
                  fills with accent when it is chosen.

                  What was here was a bare row with a checkbox, and the only
                  mark of a chosen technology was the tick itself — three pixels
                  of blue in a list of up to seventy-three. Against the picker,
                  where a chosen industry is a filled card you can find at a
                  glance, this is what read as unfinished.

                  A div with two buttons inside rather than one control, for the
                  reason the picker gives: the wide one selects and the narrow
                  one explains, and a button nested in a button is invalid
                  markup that browsers fix by dropping one of them.
                -->
            <div>
              <div
                class="flex items-center rounded-control border transition-colors {picked
                  ? 'border-accent-500 bg-accent-50'
                  : 'border-hairline bg-surface-panel hover:border-accent-300 hover:bg-gray-50'}"
              >
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-2 text-left"
                  aria-label={tech.label}
                  aria-pressed={picked}
                  on:click={() => onPick(tech.slug)}
                >
                  <span
                    class="flex {box} shrink-0 items-center justify-center rounded border {picked
                      ? 'border-accent-500 bg-accent-500 text-white'
                      : 'border-gray-300'}"
                  >
                    {#if picked}<Check class="h-3 w-3" />{/if}
                  </span>

                  <!--
                        The label in a title as well, which was missing and is a
                        bug rather than a polish: these truncate — SAP
                        SuccessFactors, Microsoft Entra ID, Adobe Commerce Cloud
                        — and a cut-off name with nothing to read it from is a
                        filter nobody can identify.
                      -->
                  <span class="min-w-0 flex-1 truncate text-sm text-gray-900" title={tech.label}>
                    {tech.label}
                  </span>
                </button>

                {#if tech.means}
                  <!--
                        Outside the selecting control, so that asking what a
                        filter means does not apply it.
                      -->
                  <button
                    type="button"
                    class="shrink-0 rounded-control px-1.5 py-1.5 text-gray-400 transition hover:text-accent-600"
                    aria-label={tech.means}
                    on:mouseenter={() => (explaining = tech.slug)}
                    on:mouseleave={() => (explaining = null)}
                    on:focus={() => (explaining = tech.slug)}
                    on:blur={() => (explaining = null)}
                  >
                    <Info class="h-3.5 w-3.5" />
                  </button>
                {/if}
              </div>

              {#if explaining === tech.slug && tech.means}
                <p class="px-2 pb-0.5 pt-1 text-xs text-ink-500">{tech.means}</p>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/each}
</div>
